import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";
import {
  FileText, CheckCircle, XCircle, Timer, Percent, PaperPlaneTilt,
  FilePdf, ArrowsClockwise, ChartBar, Buildings,
} from "@phosphor-icons/react";
import { fmtDate } from "@/lib/dateUtils";

const STATUS_LABELS = { acik: "Açık", kapali: "Kapalı" };
const STATUS_COLORS = { acik: "#F59E0B", kapali: "#10B981" };

const KpiCard = ({ icon: Icon, label, value, sub, tone = "navy" }) => {
  const bg = {
    navy: "bg-[#1B4776]",
    green: "bg-emerald-600",
    amber: "bg-amber-500",
    red: "bg-red-600",
    violet: "bg-violet-600",
    slate: "bg-slate-700",
  }[tone];
  return (
    <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 flex items-center justify-center ${bg}`}>
          <Icon size={18} weight="duotone" className="text-white" />
        </div>
      </div>
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-1">{label}</div>
      <div className="text-3xl font-black text-slate-900 font-heading">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </Card>
  );
};

const SUPPLIER_BAR_COLORS = ["#1B4776", "#95C11F", "#F59E0B", "#10B981", "#EF4444", "#8B5CF6", "#0EA5E9", "#F97316", "#14B8A6", "#DB2777"];

export default function RfqReports() {
  const { sites } = useSites();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    site_id: "all",
    status: "all",
  });

  const buildParams = () => {
    const p = {};
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    if (filters.site_id && filters.site_id !== "all") p.site_id = filters.site_id;
    if (filters.status && filters.status !== "all") p.status = filters.status;
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/rfqs/reports/summary`, { params: buildParams() });
      setData(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.site_id, filters.status]);
  useEffect(() => {
    const t = setTimeout(load, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filters.date_from, filters.date_to]);

  const exportPdf = () => {
    const url = new URL(`${API}/rfqs/report.pdf`);
    Object.entries(buildParams()).forEach(([k, v]) => url.searchParams.set(k, v));
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  };

  const kpi = data?.kpi || {
    total_rfqs: 0, total_recipients: 0, total_responded: 0, total_missing: 0,
    response_rate: 0, avg_response_hours: 0,
  };

  const statusRows = useMemo(
    () => (data?.by_status || []).map(x => ({ ...x, label: STATUS_LABELS[x.name] || x.name })),
    [data]
  );

  const supplierRows = useMemo(
    () => (data?.by_supplier || []).map(x => ({
      name: x.name.length > 22 ? x.name.slice(0, 20) + "…" : x.name,
      full: x.name,
      Gönderilen: x.sent,
      "Teklif Alınan": x.received,
    })),
    [data]
  );

  const monthRows = useMemo(
    () => (data?.by_month || []).map(x => ({
      month: x.month,
      "Talep Adedi": x.count,
    })),
    [data]
  );

  const siteRows = data?.by_site || [];

  const rows = data?.rows || [];

  return (
    <Shell
      title="Teklif Raporu"
      subtitle="Satın Alma Yönetimi"
      actions={
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={load}
            data-testid="rfq-reports-refresh"
            className="rounded-none sm:rounded-sm"
          >
            <ArrowsClockwise size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Yenile
          </Button>
          <Button
            onClick={exportPdf}
            data-testid="rfq-reports-pdf"
            className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white"
          >
            <FilePdf size={14} className="mr-1.5" /> PDF İndir
          </Button>
        </div>
      }
    >
      {/* Filtreler */}
      <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Başlangıç</Label>
            <Input type="date" value={filters.date_from}
                   onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
                   className="mt-1 rounded-none sm:rounded-sm" data-testid="rfq-filter-date-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Bitiş</Label>
            <Input type="date" value={filters.date_to}
                   onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
                   className="mt-1 rounded-none sm:rounded-sm" data-testid="rfq-filter-date-to" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Şantiye</Label>
            <Select value={filters.site_id} onValueChange={v => setFilters(f => ({ ...f, site_id: v }))}>
              <SelectTrigger className="mt-1 rounded-none sm:rounded-sm" data-testid="rfq-filter-site"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Durum</Label>
            <Select value={filters.status} onValueChange={v => setFilters(f => ({ ...f, status: v }))}>
              <SelectTrigger className="mt-1 rounded-none sm:rounded-sm" data-testid="rfq-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="acik">Açık</SelectItem>
                <SelectItem value="kapali">Kapalı</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <KpiCard icon={FileText} label="Toplam Talep" value={kpi.total_rfqs} tone="navy" />
        <KpiCard icon={PaperPlaneTilt} label="Firma Gönderimi" value={kpi.total_recipients} sub="Toplam" tone="violet" />
        <KpiCard icon={CheckCircle} label="Teklif Alınan" value={kpi.total_responded} tone="green" />
        <KpiCard icon={XCircle} label="Teklif Alınmayan" value={kpi.total_missing} tone="red" />
        <KpiCard icon={Percent} label="Yanıt Oranı" value={`%${kpi.response_rate}`} sub="Genel" tone="amber" />
        <KpiCard icon={Timer} label="Ort. Yanıt Süresi"
                 value={kpi.avg_response_hours >= 24
                   ? `${(kpi.avg_response_hours / 24).toFixed(1)} gün`
                   : `${kpi.avg_response_hours.toFixed(1)} sa`}
                 tone="slate" />
      </div>

      {/* Grafikler */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Durum Dağılımı */}
        <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white">
          <div className="flex items-center gap-2 mb-4">
            <ChartBar size={16} className="text-[#1B4776]" weight="duotone" />
            <h3 className="text-sm font-bold text-slate-900 font-heading">Durum Dağılımı</h3>
          </div>
          {statusRows.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-12">Veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusRows} dataKey="count" nameKey="label"
                     cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={2}>
                  {statusRows.map((s, i) => (
                    <Cell key={i} fill={STATUS_COLORS[s.name] || "#94A3B8"} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Aylık Trend */}
        <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white">
          <div className="flex items-center gap-2 mb-4">
            <ChartBar size={16} className="text-[#1B4776]" weight="duotone" />
            <h3 className="text-sm font-bold text-slate-900 font-heading">Aylık Talep Trendi</h3>
          </div>
          {monthRows.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-12">Veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={monthRows}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="Talep Adedi" stroke="#1B4776" strokeWidth={2}
                      dot={{ fill: "#95C11F", r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Firma Bazında Gönderim vs Alım */}
        <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <ChartBar size={16} className="text-[#1B4776]" weight="duotone" />
            <h3 className="text-sm font-bold text-slate-900 font-heading">
              Firma Bazında Yanıt Durumu (Top 10)
            </h3>
          </div>
          {supplierRows.length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-12">Veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={supplierRows} margin={{ bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip formatter={(v, k, item) => [v, k]}
                         labelFormatter={(l, payload) => payload && payload[0] ? payload[0].payload.full : l} />
                <Legend />
                <Bar dataKey="Gönderilen" fill="#1B4776" />
                <Bar dataKey="Teklif Alınan" fill="#95C11F" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Şantiye Dağılımı */}
      {siteRows.length > 0 && (
        <Card className="p-5 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white mb-4">
          <div className="flex items-center gap-2 mb-4">
            <Buildings size={16} className="text-[#1B4776]" weight="duotone" />
            <h3 className="text-sm font-bold text-slate-900 font-heading">Şantiye Bazında Talep Sayısı</h3>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={siteRows} layout="vertical" margin={{ left: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={200} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" name="Talep Sayısı" fill="#1B4776" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* Detay Tablo */}
      <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 shadow-sm bg-white overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h3 className="text-sm font-bold text-slate-900 font-heading">Talep Detayları</h3>
          <p className="text-xs text-slate-500 mt-0.5">{rows.length} kayıt</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Talep No", "Talep Formu", "Şantiye", "Talep Tarihi", "Talep Eden", "Durum", "Firma", "Yanıt", "Yanıt %"].map(h => (
                  <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-widest font-bold text-slate-600 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id || i}
                    className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`rfq-report-row-${i}`}>
                  <td className="px-3 py-2 font-bold text-[#1B4776]">{r.code}</td>
                  <td className="px-3 py-2 max-w-[260px] truncate" title={r.talep_formu}>{r.talep_formu}</td>
                  <td className="px-3 py-2">{r.site_name}</td>
                  <td className="px-3 py-2">{fmtDate(r.request_date)}</td>
                  <td className="px-3 py-2">{r.requester_name}</td>
                  <td className="px-3 py-2">
                    <span
                      className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white"
                      style={{ backgroundColor: STATUS_COLORS[r.status] || "#94A3B8" }}
                    >
                      {STATUS_LABELS[r.status] || r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">{r.recipients_count}</td>
                  <td className="px-3 py-2 text-center">{r.responded_count}</td>
                  <td className="px-3 py-2 text-center font-bold text-slate-700">
                    %{r.response_rate.toFixed(0)}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-3 py-8 text-center text-xs text-slate-400">
                    Kayıt yok.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </Shell>
  );
}
